import { Label } from '@/components/ui/label';
import { ReferencePickerField } from '@/components/forms/ReferencePickerField';
import { TabularSectionEditor } from '@/components/autoforms/TabularSectionEditor';
import { CopyButton } from '@/components/ui/CopyButton';
import { HelpHint } from '@/components/ui/HelpHint';

// ============================================================================
// AutoFormContent — содержимое АВТО-формы объекта (вынесено из AutoObjectForm,
// E2e8): иерархия справочника (Группа/Родитель), поле «Владелец» (составная
// ссылка), реквизиты по order/hidden_by_default (renderField родителя), табличные
// части и системный справочник «Файлы» (загрузка/скачивание). ЧИСТО
// отображательный компонент (SRP): вся запись/состояние в AutoObjectForm.
// ============================================================================

export interface AutoFieldDef {
  id?: string;
  code?: string;
  name?: string;
  synonym?: string;
  dataType?: string;
  data_type?: string;
  isRequired?: boolean;
  is_required?: boolean;
  order?: number;
  refTable?: string;
  ref_table?: string;
  refCatalogCode?: string;
  hiddenByDefault?: boolean;
  hidden_by_default?: boolean;
  types?: string[];
}

export interface AutoTsColumnDef {
  name: string;
  dataType?: string;
  refTable?: string;
  refCatalogCode?: string;
  isRequired?: boolean;
  selectionParams?: Array<{ field: string; condition: string; value?: string }>;
  selectionLinks?: Array<{ param: string; field: string }>;
  synonym?: string;
}

export interface AutoTsDef {
  id?: string;
  code?: string;
  name?: string;
  synonym?: string;
  columns?: AutoTsColumnDef[];
}

interface AutoFormContentProps {
  validationError: string | null;
  fields: AutoFieldDef[];
  data: Record<string, any>;
  onDataChange: (updater: (prev: Record<string, any>) => Record<string, any>) => void;
  fieldErrors: Record<string, string>;
  /** Рендер реквизита (родитель передаёт renderField — классификатор + ветки) */
  renderField: (f: AutoFieldDef) => React.ReactNode;
  hierarchyType: string;
  owners: Array<{ code: string; type: string }>;
  objectName: string;
  tabularSections: AutoTsDef[];
  tsParentId: string;
  readOnly: boolean;
  isFileCatalog: boolean;
  fileBusy: boolean;
  fileError: string | null;
  fileLocked: boolean;
  fileDownloadable: boolean;
  fileDisplayName: string;
  fileSizeText: string;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFileDownload: () => void;
}

const SYSTEM_SKIP = ['id', 'created_at', 'updated_at', 'created_by', 'updated_by', 'is_marked', 'is_posted', 'posted_at'];

export function AutoFormContent({
  validationError, fields, data, onDataChange, fieldErrors, renderField, hierarchyType,
  owners, objectName, tabularSections, tsParentId, readOnly, isFileCatalog, fileBusy,
  fileError, fileLocked, fileDownloadable, fileDisplayName, fileSizeText,
  onFileUpload, onFileDownload,
}: AutoFormContentProps) {
  return (
    <>
      {validationError && (
        <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2">
          <span className="flex-1 min-w-0 break-words">{validationError}</span>
          <CopyButton text={validationError} compact />
        </div>
      )}
      {fields.length === 0 && (
        <div className="text-xs text-muted-foreground italic">Нет реквизитов в метаданных</div>
      )}
      {/* Иерархия справочника: Группа + Родитель */}
      {hierarchyType === 'groups_and_items' && (
        <label className="flex items-center gap-2 text-xs">
          <input
            type="checkbox"
            checked={!!data.is_group}
            onChange={(e) => onDataChange((prev) => ({ ...prev, is_group: e.target.checked }))}
            className="rounded"
          />
          Группа
        </label>
      )}
      {hierarchyType && (
        <div className="space-y-1">
          <Label className="text-xs">Родитель</Label>
          <ReferencePickerField
            value={data.parent_id ?? ''}
            refTable={objectName}
            displayValue={String(data.parent_id_presentation ?? '')}
            selectionMode={hierarchyType === 'items' ? 'items' : 'both'}
            onChange={(v) => { onDataChange((prev) => ({ ...prev, parent_id: v })); }}
          />
        </div>
      )}
      {/* Владельцы справочника: ОДНО поле «Владелец» — составная ссылка
          на все объекты-владельцы: справочники/документы/определяемые типы/
          пользователи (entity — выбор из списка пользователей, entities) */}
      {owners.length > 0 && (
        <div className="space-y-1">
          <Label className="text-xs">Владелец</Label>
          <ReferencePickerField
            value={data.owner_id ?? ''}
            types={owners.map((o) => {
              if (o.type === 'document') return `Документ.${o.code}`;
              if (o.type === 'entity') return `entity.${o.code}`;
              return `Справочник.${o.code}`;
            })}
            displayValue={String(data.owner_id_presentation ?? '')}
            selectionMode={''}
            onChange={(v) => { onDataChange((prev) => ({ ...prev, owner_id: v })); }}
          />
        </div>
      )}
      {fields
        .filter((f) => !SYSTEM_SKIP.includes(f.code || f.name || ''))
        // Скрытые по умолчанию (как в 1С): «Код» и др. служебные поля в АВТО-форме
        // не выводятся (присваиваются автоматически); ЯВНЫЙ макет показывает их
        .filter((f) => !f.hiddenByDefault && !(f as any).hidden_by_default)
        .sort((a, b) => (a.order || 0) - (b.order || 0))
        .map((f) => (
          <AutoFieldHint key={f.code || f.name} field={f} err={!!fieldErrors[f.code || f.name || '']}>
            {renderField(f)}
          </AutoFieldHint>
        ))}

      {tabularSections.length > 0 && (
        <>
          {tabularSections.map((ts) => {
            const tsTable = `${objectName}_${(ts.code || ts.name || '').toLowerCase()}`;
            return (
              <TabularSectionEditor
                key={ts.id || ts.code}
                tableName={tsTable}
                synonym={ts.synonym || ts.name || ts.code}
                columns={(ts.columns || []).map((c) => ({
                  name: c.name,
                  dataType: c.dataType || 'text',
                  refTable: c.refTable || (c as any).refCatalogCode || '',
                  isRequired: (c as any).isRequired,
                  selectionParams: (c as any).selectionParams,
                  selectionLinks: (c as any).selectionLinks,
                }))}
                parentId={tsParentId}
                readOnly={readOnly}
              />
            );
          })}
        </>
      )}

      {/* Системный справочник «ФайлыПоДокументам»: загрузка/скачивание содержимого */}
      {isFileCatalog && (
        <div className="border rounded-md p-3 space-y-2 bg-muted/20">
          <div className="text-xs font-medium">Файл</div>
          <div className="text-xs text-muted-foreground">
            {fileDisplayName}
            {fileSizeText}
          </div>
          <div className="flex gap-2">
            <label className="text-xs border rounded px-2 py-1 cursor-pointer hover:bg-muted/30">
              {fileBusy ? 'Загрузка…' : 'Загрузить файл'}
              <input type="file" className="hidden" onChange={onFileUpload} disabled={fileBusy || fileLocked} />
            </label>
            {fileDownloadable && (
              <button className="text-xs border rounded px-2 py-1 hover:bg-muted/30" onClick={onFileDownload} disabled={fileBusy}>
                Скачать
              </button>
            )}
          </div>
          {fileError && <div className="text-xs text-destructive">{fileError}</div>}
        </div>
      )}
    </>
  );
}

// ============================================================================
// AutoFieldHint — подсказка реквизита (как в 1С) в авто-форме: hover (title при
// наведении) / bottom (под полем) / button (кнопка «?» — ПОПАП НАД ЭЛЕМЕНТОМ,
// универсальный HelpHint); placeholder — внутри поля. При ОШИБКЕ заполнения (err)
// подсказка показывается ПОД полем всегда (если задана).
// ============================================================================
function AutoFieldHint({ field, children, err }: { field: AutoFieldDef; children: React.ReactNode; err?: boolean }) {
  const hint = String((field as any)?.hint || '');
  const pos = String((field as any)?.hintPosition || (field as any)?.hint_position || '');
  if (!hint || pos === '') {
    // Ошибка + есть подсказка → показываем её под полем (максимально понятно)
    if (err && hint) {
      return (
        <div className="space-y-0.5">
          {children}
          <div className="text-[10px] text-muted-foreground leading-snug">{hint}</div>
        </div>
      );
    }
    return <>{children}</>;
  }
  if (err || pos === 'bottom') {
    return (
      <div className="space-y-0.5">
        {children}
        <div className="text-[10px] text-muted-foreground leading-snug">{hint}</div>
      </div>
    );
  }
  if (pos === 'hover') return <div title={hint}>{children}</div>;
  if (pos === 'button') {
    return (
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">{children}</div>
        <span className="mt-1.5 shrink-0">
          <HelpHint text={hint} />
        </span>
      </div>
    );
  }
  return <>{children}</>;
}
